import cn from 'classnames';
import React, { memo, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Icon } from 'components';
import ImageUpload from 'components/ImageUpload';
import { Col, Row } from 'components/layout';
import { InputV2 } from 'components/ComponentV2';
import { REGEX } from 'lib/utils/regex';

import Gamification, { GamificationProps } from '../Gamification';
import SectionWrapper from '../SectionWrapper';

import styles from './BasicDetails.module.scss';

export interface BasicDetailsProps extends GamificationProps {
  logo: string;
  showProgressBanner: boolean;
  sectionClass?: string;
}

export const BasicDetails: React.FC<BasicDetailsProps> = memo(
  ({
    logo,
    showProgressBanner,
    progressDetails,
    profileCompletePercent,
    nonCompletedSection,
    handleProfileCompletionButton,
    handleNextButton,
    showNextButton,
    sectionClass,
  }: BasicDetailsProps) => {
    const {
      register,
      watch,
      setValue,
      formState: { errors },
    } = useFormContext(); // retrieve all hook methods
    const icon = watch('logo');

    const [showProgressBannerState, setShowProgressBannerState] =
      React.useState<boolean>(showProgressBanner);

    useEffect(() => {
      if (showProgressBannerState === false)
        setShowProgressBannerState(showProgressBanner);
    }, [showProgressBanner]);

    useEffect(() => {
      register('logo');
    }, [register]);

    const [showLogoFileSizeError, setShowLogoFileSizeError] =
      useState<boolean>(false);

    const onChangeLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      const files: FileList = target.files as FileList;
      if (files[0].size / 1000 < 5001) {
        setShowLogoFileSizeError(false);
        setValue('logo', files, { shouldDirty: true });
      } else {
        setShowLogoFileSizeError(true);
      }
    };

    const logoImage =
      typeof icon === 'object' && icon?.length > 0
        ? URL.createObjectURL(icon[0])
        : typeof icon === 'string'
        ? icon
        : logo;

    const defaultLogoImage = 'upload-photo.png';

    return (
      <div id={`section_${progressDetails?.id}`} className={sectionClass}>
        {showProgressBannerState && (
          <Gamification
            profileCompletePercent={profileCompletePercent}
            nonCompletedSection={nonCompletedSection}
            progressDetails={progressDetails}
            handleProfileCompletionButton={() => {
              setShowProgressBannerState(false);
              handleProfileCompletionButton();
            }}
            handleNextButton={() => {
              setShowProgressBannerState(false);
              handleNextButton && handleNextButton();
            }}
            showNextButton={showNextButton}
          />
        )}
        <SectionWrapper
          title="Basic Info"
          description="Add your basic info here so that students can identify your company with ease."
          className={cn({ [styles.noBorderRadius]: showProgressBannerState })}
        >
          <Row className={styles.BasicDetailsLogo} justify="space-between">
            <Col>
              <ImageUpload
                image={logoImage || defaultLogoImage}
                alt={'Society profile image'}
                onChange={onChangeLogo}
                className={styles.BasicDetailsLogoImage}
                label={logoImage ? 'Change logo' : 'Add logo'}
                imageSrc={logoImage ? undefined : 'aws'}
              />
            </Col>
            {showLogoFileSizeError ? (
              <div className={styles['logo-error-container']}>
                <Icon iconName={'info'}></Icon>
                <span>
                  Please upload an image with a size of less than 5 MB
                </span>
              </div>
            ) : (
              <></>
            )}
          </Row>
          <Row>
            <Col span={24} className={styles.BasicDetailsFormColumn}>
              <InputV2
                label="Company Name"
                {...register('name', {
                  required: 'Name is required',
                })}
                isInvalid={!!errors.name}
                helperText={errors.name?.message}
                labelClassName={styles.labelField}
                inputClassName={styles.inputField}
                placeholder="Add your Company name"
                maxLength={120}
              />
            </Col>
            <Col span={24} className={styles.BasicDetailsFormColumn}>
              <InputV2
                label="Website"
                {...register('website', {
                  required: true,
                  pattern: {
                    value: REGEX.URL,
                    message: 'Enter a valid url',
                  },
                })}
                isInvalid={!!errors.website}
                helperText={errors.website?.message}
                labelClassName={styles.labelField}
                inputClassName={styles.inputField}
                placeholder="Add your Website URL"
              />
            </Col>
            <Col span={24}>
              <InputV2
                label="Explain what your company does in one sentence"
                {...register('short_description', {
                  required: 'Short description is required',
                })}
                isInvalid={!!errors.short_description}
                helperText={errors.short_description?.message}
                labelClassName={styles.labelField}
                inputClassName={styles.inputField}
                placeholder="Early-careers platform fueling the next generation of young talent"
                maxLength={120}
              />
            </Col>
          </Row>
        </SectionWrapper>
      </div>
    );
  }
);

BasicDetails.displayName = 'BasicDetails';
